import { useIsCanvasFullWidth } from '@/atoms/canvasReadableWidthAtom';
import { CODE_SYSTEM_CONFIG } from '@/components/constants/clinical-coding';
import { IS_CLINICAL_CODING_ENABLED } from '@/components/constants/flags';
import { useIsV2Layout } from '@/container/session-page/useV2Layout';
import { bodyColumnClass } from '@/container/session-page/v2/main/v2-canvas-tokens';
import { useQuerySessionClinicalCodes } from '@/hooks/query/clinical-codes/useQuerySessionClinicalCodes';
import { cn } from '@/lib/cn';
import { typedEntries } from '@/lib/objectUtils';
import { CodedClinicalEntityStatus } from '@/types/schema';
import { CodeSystem, type CodeSystem as CodeSystemType } from '@heidi/coding/contracts';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { memo, useMemo } from 'react';
import CodeGroup from './CodeGroup';

const EmbeddingsCodesets = () => {
  const sessionId = useCurrentSessionId();
  const isV2Layout = useIsV2Layout();
  const isCanvasFullWidth = useIsCanvasFullWidth();
  const { data: codes } = useQuerySessionClinicalCodes(sessionId);

  const codesToShow = codes?.clinical_entities?.filter(
    (code) => code.status === CodedClinicalEntityStatus.CONFIRMED
  );

  const codesBySystem = useMemo(() => {
    const codesBySystem = new Map<CodeSystemType, typeof codesToShow>();
    if (!codesToShow) return codesBySystem;

    typedEntries(CODE_SYSTEM_CONFIG).forEach(([codeSystem]) =>
      codesBySystem.set(
        codeSystem,
        codesToShow.filter((c) => c.primary_code.code_system === codeSystem)
      )
    );
    return codesBySystem;
  }, [codesToShow]);

  if (!IS_CLINICAL_CODING_ENABLED()) {
    return null;
  }

  if (codesToShow?.length === 0 || !codesToShow) {
    return null;
  }

  return (
    <div
      className={cn(
        'mt-4 flex flex-col gap-2',
        isV2Layout && bodyColumnClass(isCanvasFullWidth)
      )}
    >
      {typedEntries(CODE_SYSTEM_CONFIG)
        .filter(([codeSystem]) => codeSystem !== CodeSystem.e_m)
        .map(([codeSystem, config]) => (
          <CodeGroup
            key={codeSystem}
            label={config.nameWithCodingSuffix}
            codes={codesBySystem.get(codeSystem) ?? []}
          />
        ))}
    </div>
  );
};

export default memo(EmbeddingsCodesets);
